import { TbCheck, TbCircleDot, TbFileText, TbMicrophone, TbSend } from "solid-icons/tb";
import { type Component, For, Show } from "solid-js";
import { LinearLogo } from "~/components/integrations/IntegrationSection";
import {
  GmailIcon,
  GoogleCalendarIcon,
  NotionIcon,
  SlackIcon,
} from "~/components/knowledge/pages/ScopePickerEmptyState";
import { useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import "~/components/marketing/mockups/mockups.css";

type Moment = {
  label: TranslationKey;
  time: string;
  title: TranslationKey;
  status: TranslationKey;
  icon: Component<{ class?: string }>;
  color: string;
  items: { icon: Component<{ class?: string }>; text: TranslationKey; done?: boolean }[];
};

const MOMENTS: Moment[] = [
  {
    label: "marketing.executives.day.before",
    time: "09:55",
    title: "marketing.mock.exec.brief",
    status: "marketing.mock.exec.ready",
    icon: TbFileText,
    color: "#2f6bff",
    items: [
      { icon: NotionIcon, text: "marketing.mock.exec.page" },
      { icon: TbCircleDot, text: "marketing.mock.roundtrip.brief1" },
      { icon: TbCircleDot, text: "marketing.mock.roundtrip.brief2" },
    ],
  },
  {
    label: "marketing.executives.day.during",
    time: "10:00",
    title: "marketing.executives.day.during_title",
    status: "marketing.executives.day.during_status",
    icon: TbMicrophone,
    color: "#e5484d",
    items: [
      { icon: TbCheck, text: "marketing.mock.roundtrip.summary1" },
      { icon: TbCheck, text: "marketing.mock.roundtrip.summary2" },
      { icon: LinearLogo, text: "marketing.mock.offer.issue" },
    ],
  },
  {
    label: "marketing.executives.day.after",
    time: "10:42",
    title: "marketing.executives.day.after_title",
    status: "marketing.mock.exec.sent",
    icon: TbSend,
    color: "#1d9e75",
    items: [
      { icon: GmailIcon, text: "marketing.mock.exec.email", done: true },
      { icon: SlackIcon, text: "marketing.mock.exec.slack", done: true },
      { icon: GoogleCalendarIcon, text: "marketing.mock.exec.invite", done: true },
    ],
  },
];

/**
 * One meeting's arc from the executive's side, prepped, captured and followed
 * through, as a timeline that fills in once when scrolled into view. Each node
 * sits over its card's centre and the rail runs node to node; phones get the
 * same rail down the left.
 */
export const DayStrip = () => {
  const { t } = useI18n();
  const scene = useScene();
  return (
    <ol
      ref={scene.ref}
      class="mk-scene grid gap-4 text-start md:grid-cols-3"
      classList={{ "mk-armed": scene.armed(), "in-view": scene.inView() }}
    >
      <For each={MOMENTS}>
        {(moment, i) => (
          <li class="relative min-w-0 ps-14 md:ps-0 md:pt-16" style={{ "--c": moment.color }}>
            <span
              class="pop-in mk-ink absolute inset-s-0 top-0 grid size-10 place-items-center rounded-xl bg-[color-mix(in_oklab,var(--c)_14%,var(--color-tile))] shadow-sm ring-4 ring-canvas md:inset-s-auto md:left-1/2 md:-translate-x-1/2"
              style={{ "animation-delay": `${0.2 + i() * 0.7}s` }}
            >
              <moment.icon class="size-5" aria-hidden="true" />
            </span>
            <Show when={i() < MOMENTS.length - 1}>
              <span
                aria-hidden="true"
                class="absolute inset-s-5 top-12 -bottom-2 w-0.5 -translate-x-1/2 overflow-hidden rounded-full bg-primary-border md:inset-s-[calc(50%+1.75rem)] md:inset-e-[calc(-50%+0.75rem)] md:top-5 md:bottom-auto md:h-0.5 md:w-auto md:translate-x-0"
              >
                <span
                  class="rail-grow absolute inset-0 origin-top bg-linear-to-b from-(--c) to-primary-border [--gy:0] md:origin-left md:bg-linear-to-r md:[--gx:0] md:[--gy:1]"
                  style={{ "animation-delay": `${0.5 + i() * 0.7}s` }}
                />
              </span>
            </Show>
            <article
              class="card-appear flex h-full flex-col gap-3 rounded-2xl border border-primary-border bg-tile p-5 shadow-sm"
              style={{ "animation-delay": `${0.3 + i() * 0.15}s` }}
            >
              <p class="flex items-center justify-between font-mono text-[11px] font-semibold tracking-wider text-muted-text uppercase">
                <span class="mk-ink">{t(moment.label)}</span>
                <span class="font-normal tabular-nums">{moment.time}</span>
              </p>
              <h3 class="font-semibold">{t(moment.title)}</h3>
              <ul class="flex flex-col gap-2 border-t border-primary-border pt-3 text-[13px] leading-snug">
                <For each={moment.items}>
                  {(item, j) => (
                    <li
                      class="msg-in flex items-center gap-2"
                      style={{ "animation-delay": `${0.6 + i() * 0.7 + j() * 0.15}s` }}
                    >
                      <span class="mk-ink grid size-5 shrink-0 place-items-center rounded-md bg-canvas">
                        <item.icon class="size-3.5" aria-hidden="true" />
                      </span>
                      <span class="min-w-0 flex-1 truncate text-secondary-text">{t(item.text)}</span>
                      <Show when={item.done}>
                        <TbCheck class="size-3.5 shrink-0 text-marketing-success" aria-hidden="true" />
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
              <span class="mk-ink mt-auto inline-flex items-center gap-1.5 self-start rounded-full bg-(--c)/12 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase ring-1 ring-(--c)/20">
                <span class="size-1.5 rounded-full bg-(--c)" />
                {t(moment.status)}
              </span>
            </article>
          </li>
        )}
      </For>
    </ol>
  );
};
